import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MetaTag from 'components/meta';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import ConsentFormFillable from 'components/consentForm/ConsentFormFillable';
import useCommuniqueState from 'hooks/useCommuniqueState';
import { usePatientFormWorkflow } from 'hooks/usePatientFormWorkflow';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { RootState } from 'store/store';
import { isTabletBaseIntake } from 'env';
import { useEnsureFormWorkflowForAppointmentCommunique } from 'hooks/useApi';
import { isEmptyString, parseErrorMessage } from 'utils/helper';
import { ModuleTypes } from 'types/appState';

type ConsentFormAppProps = {
  onDisplayChooseScreen?: () => void;
};

function ConsentFormApp({ onDisplayChooseScreen }: ConsentFormAppProps = {}) {
  const dispatch = useDispatch();
  const { communiqueData, setCommuniqueData } = useCommuniqueState();
  const appState = useSelector((state: RootState) => state.appStateSettings);
  const pendingTarget = appState?.pendingTarget;
  const pccConsentWorkflowId = appState?.pccConsentWorkflowId;
  const isPccConsent = !isNil(pccConsentWorkflowId);
  const [formWorkflowError, setFormWorkflowError] = useState('');
  const ensureAttemptedTokenRef = useRef<string | null>(null);
  const {
    mutateAsync: ensureFormWorkflow,
    isPending: isLoadingFormWorkflow,
  } = useEnsureFormWorkflowForAppointmentCommunique();

  const formWorkflowId = pccConsentWorkflowId ?? communiqueData?.formWorkflowId;
  const isAlreadyCompleted = isPccConsent
    ? false
    : communiqueData?.sendForms?.consentForm === false;

  useEffect(() => {
    const token = communiqueData?.token ?? '';
    const shouldTriggerEnsureWorkflowApiCall = !isPccConsent
      && !isAlreadyCompleted
      && isNil(formWorkflowId)
      && !isEmptyString(token)
      && ensureAttemptedTokenRef.current !== token;

    if (!shouldTriggerEnsureWorkflowApiCall) return;

    ensureAttemptedTokenRef.current = token;
    setFormWorkflowError('');

    const ensureAppointmentFormWorkflow = async () => {
      try {
        const response = await ensureFormWorkflow({ token });

        if (!isNil(communiqueData)) {
          setCommuniqueData({
            ...communiqueData,
            formWorkflowId: response.formWorkflowId,
          });
        }
      } catch (error) {
        setFormWorkflowError(parseErrorMessage(error, 'Unable to load consent forms. Please refresh and try again.'));
      }
    };

    ensureAppointmentFormWorkflow();
  }, [
    communiqueData,
    ensureFormWorkflow,
    formWorkflowId,
    isAlreadyCompleted,
    isPccConsent,
    setCommuniqueData,
  ]);

  const {
    data,
    loading,
    errorMessages,
    signedMultimedia,
    onSubmit,
  } = usePatientFormWorkflow({
    formWorkflowId,
    enabled: !isAlreadyCompleted && !isNil(formWorkflowId),
    onSubmitSuccess: () => {
      if (!isPccConsent && !isNil(communiqueData)) {
        setCommuniqueData({
          ...communiqueData,
          sendForms: {
            ...communiqueData.sendForms,
            consentForm: false,
          },
        });
      }
      if (pendingTarget) {
        dispatch(appStateSettingsAction({
          ...pendingTarget,
          pendingTarget: null,
          pccConsentWorkflowId: null,
        }));
      } else if (onDisplayChooseScreen) {
        dispatch(appStateSettingsAction({
          pccConsentWorkflowId: null,
          pendingTarget: null,
        }));
        onDisplayChooseScreen();
      } else if (isPccConsent || isTabletBaseIntake || !isNil(appState?.appointmentToken)) {
        dispatch(appStateSettingsAction({
          name: ModuleTypes.communiqueByPatientInfo,
          page: 'choice',
          pccConsentWorkflowId: null,
          pendingTarget: null,
        }));
      }
    },
  });

  const displayErrorMessages = useMemo(
    () => (formWorkflowError || (isNil(formWorkflowId)
      && !isAlreadyCompleted
      && !loading
      && !isLoadingFormWorkflow
      ? 'Unable to load consent forms. Please refresh and try again.'
      : errorMessages)),
    [
      formWorkflowError,
      errorMessages,
      formWorkflowId,
      isAlreadyCompleted,
      isLoadingFormWorkflow,
      loading,
    ],
  );

  return (
    <div className={clsx(
      'text-md',
      { '!max-w-[500px] m-auto bg-white': !isNil(signedMultimedia) },
    )}
    >
      <MetaTag title={loading ? 'Loading...' : 'Consent forms'} />

      <ConsentFormFillable
        data={data}
        loading={loading || isLoadingFormWorkflow}
        errorMessages={displayErrorMessages}
        signedMultimedia={signedMultimedia}
        onSubmit={onSubmit}
        compactSignedLayout
      />
    </div>
  );
}

export default ConsentFormApp;
